<script setup lang="ts">
import { computed } from 'vue';

import { BaseAvatar, BaseBadge } from '@magma-app/magma-lapilli';

import type { HelperCampaignAdmin } from '../types';

const props = defineProps<{
  campaignAdmin: HelperCampaignAdmin | null;
  helpersProfilePicture: string[];
  helpersCount: number;
  points: number;
  showBack: boolean;
}>();

const hasAdmin = computed(() =>
  Boolean(props.campaignAdmin?.firstname || props.campaignAdmin?.lastname)
);

const adminFallback = computed(() => {
  const firstnameInitial = props.campaignAdmin?.firstname?.charAt(0) || '';
  const lastnameInitial = props.campaignAdmin?.lastname?.charAt(0) || '';
  return `${firstnameInitial}${lastnameInitial}`;
});

const groupAvatars = computed(() =>
  props.helpersProfilePicture.map((helper) => ({ url: helper })).slice(0, 4)
);

const badgesClass = computed(() => [
  'flex items-center',
  {
    'mt-4': props.showBack,
  },
]);
</script>

<template>
  <div :class="['mt-4 flex', hasAdmin ? 'justify-between' : 'justify-end']">
    <div v-if="hasAdmin">
      <span class="text-mgm-txt-sm font-normal text-foreground-default">
        {{ $t('generic.invited') }}
      </span>

      <div class="mt-1 flex items-center">
        <BaseAvatar
          :src="campaignAdmin?.profilePictureUrl"
          :alt="`${$t('generic.profilePicture')} ${
            campaignAdmin?.firstname
          } ${campaignAdmin?.lastname}`"
          :fallback="adminFallback"
          size="sm"
        />

        <span class="ml-2 text-mgm-txt-sm font-medium text-foreground-emphasis">
          {{
            (campaignAdmin?.firstname ? `${campaignAdmin?.firstname} ` : '') +
            (campaignAdmin?.lastname ? campaignAdmin?.lastname : '')
          }}
        </span>
      </div>
    </div>

    <div v-if="helpersProfilePicture && helpersProfilePicture.length > 0">
      <span
        class="block text-right text-mgm-txt-sm font-normal text-foreground-default"
      >
        {{ $t('generic.community') }}
      </span>

      <BaseAvatar
        class="mt-1 justify-end"
        :group-avatars-urls="groupAvatars"
        size="sm"
        type="group"
        plus
      />
    </div>
  </div>

  <div :class="badgesClass">
    <BaseBadge
      v-if="points"
      class="mr-2"
      :text="`${points}`"
      size="md"
      icon-type="icon"
      icon-name="star"
    />

    <BaseBadge
      v-if="helpersCount"
      class="text-mgm-txt-sm font-medium"
      :text="`${helpersCount} helpers`"
      size="md"
      color="purple"
      icon-type="icon"
      icon-name="users"
    />
  </div>
</template>
